import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Breadcrumb } from '@tpct/admin-ui';
import type { BreadcrumbItem } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import { sanitizeIntegrationListPayload } from '@/hub-shared/utils/integrationConnections';
import { extractErrorMessage, isSessionExpired } from '../api/apiErrors';
import { fetchConnections, fetchImportableFields, previewIntegrationMapping, type PreviewCompany } from '../api/integrationCompanies';
import { getEntityKeysMapFromCache } from './entityKeys';
import { getProfileImportFieldIdsForConnection } from '../companyProfileImportFieldCatalog';
import type { IntegrationApi } from '../connectionTypes';
import type { PortalBootstrapData } from '../types/window';
import { ImportConfirmModal } from './components/ImportConfirmModal';
import { ImportWizardIntegrationSidebar } from './ImportWizardIntegrationSidebar';
import { WizardShell } from './WizardShell';
import { useImportJob } from './hooks/useImportJob';
import { useConflictsSummary } from './hooks/useConflictsSummary';
import { usePersistWizardState, useWizardStorage } from './hooks/useWizardStorage';
import { ConflictsPlaceholderStep } from './steps/ConflictsPlaceholderStep';
import { SelectDataStep } from './steps/SelectDataStep';
import { SelectIntegrationCompaniesStep } from './steps/SelectIntegrationCompaniesStep';
import type {
  EntityKey,
  ImportWizardState,
  MappingPreviewItem,
  IntegrationCompaniesResponse,
  IntegrationSelectionState,
  WizardMode,
} from './types';
import type { PortalSearchSyncFn } from '../utils/portalUrlState';
import { parsePortalSearchParams, pickAllowedParam } from '../utils/portalUrlState';

type ImportWizardPageProps = {
  navigateTo: (path: string) => void;
  bootstrap: PortalBootstrapData;
  companyId: number | null;
  onSessionExpired: () => void;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const IMPORT_STEP_SLUGS = ['select-companies', 'select-data', 'conflicts'] as const;

const normalizePath = (value: string) => {
  if (value.length > 1 && value.endsWith('/')) {
    return value.slice(0, -1);
  }
  return value;
};

const IMPORT_WIZARD_LAST_STEP_INDEX = 2;

const createDefaultState = (mode: WizardMode, targetCompanyId: number | null): ImportWizardState => ({
  version: 3,
  mode,
  targetCompanyId,
  selectionsByConnectionId: {},
  attachTargetByRemoteId: {},
  allowCreateByRemoteId: {},
  mapping: {
    confirmedAtIso: null,
    itemsByConnectionId: {},
  },
  run: {
    startedAtIso: null,
    stopRequested: false,
    status: 'idle',
    items: [],
  },
  conflicts: {
    status: 'idle',
    items: [],
  },
});

const buildPreviewCompanies = (
  remoteCompanyIds: string[],
  entityKeysByRemoteId: Map<string, EntityKey[]>,
): PreviewCompany[] =>
  remoteCompanyIds.map((id) => ({
    remote_company_id: id,
    entity_keys: entityKeysByRemoteId.get(id) ?? [],
  }));

export const ImportWizardPage = ({
  navigateTo,
  bootstrap,
  companyId,
  onSessionExpired,
  search,
  patchPortalSearch,
}: ImportWizardPageProps) => {
  const mode: WizardMode = companyId === null ? 'bulk' : 'single';
  const portalMode = typeof patchPortalSearch === 'function';
  const portalImportParams = useMemo(() => parsePortalSearchParams(search ?? ''), [search]);
  const inferredStepIndex = useMemo(() => {
    if (!portalMode) {
      return 0;
    }
    const raw = portalImportParams.get('step');
    if (raw === null || raw === '') {
      return 0;
    }
    const slug = pickAllowedParam(raw, IMPORT_STEP_SLUGS, 'select-companies');
    return IMPORT_STEP_SLUGS.indexOf(slug);
  }, [portalImportParams, portalMode]);
  const { storageKey, loadStoredState, clearState } = useWizardStorage(
    bootstrap.cabinetId,
    bootstrap.currentUser.id,
    mode,
    companyId,
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [state, setState] = useState<ImportWizardState>(() => {
    const stored = loadStoredState(mode, companyId);
    return stored ?? createDefaultState(mode, companyId);
  });

  const [mappingLoadingByConnectionId, setMappingLoadingByConnectionId] = useState<Record<string, boolean>>({});
  const [mappingErrorByConnectionId, setMappingErrorByConnectionId] = useState<Record<string, string>>({});
  const [pickerConnectionId, setPickerConnectionId] = useState<string | null>(null);

  const prevRunStatusRef = useRef(state.run.status);

  useEffect(() => {
    if (!portalMode) {
      return;
    }
    setStepIndex(inferredStepIndex);
  }, [inferredStepIndex, portalMode]);

  useEffect(() => {
    if (!portalMode) {
      return;
    }
    setImportModalOpen(portalImportParams.get('importModal') === 'confirm');
  }, [portalImportParams, portalMode]);

  usePersistWizardState(state, storageKey);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const getState = useCallback(() => stateRef.current, []);

  const navigateStepDelta = useCallback(
    (nextIdx: number) => {
      setStepIndex(nextIdx);
      if (portalMode && patchPortalSearch) {
        const slug = IMPORT_STEP_SLUGS[nextIdx];
        patchPortalSearch({ step: nextIdx === 0 ? null : slug }, 'push');
      }
    },
    [patchPortalSearch, portalMode],
  );

  const closeConfirmModalOnly = useCallback(() => {
    if (portalMode && patchPortalSearch) {
      patchPortalSearch({ importModal: null }, 'replace');
    }
    setImportModalOpen(false);
  }, [patchPortalSearch, portalMode]);

  const handleCancelWizard = useCallback(() => {
    if (mode === 'single' && companyId !== null) {
      navigateTo(`/companies/company/${companyId}`);
      return;
    }
    navigateTo('/companies');
  }, [companyId, mode, navigateTo]);

  useEffect(() => {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      setState(createDefaultState(mode, companyId));
      return;
    }
    const stored = loadStoredState(mode, companyId);
    setState(stored ?? createDefaultState(mode, companyId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const connectionsQuery = useQuery({
    queryKey: ['connections-list', bootstrap.restUrls.accountingHub],
    queryFn: async () => {
      const payload = await fetchConnections({
        baseUrl: bootstrap.restUrls.accountingHub,
        nonce: bootstrap.nonce,
      });
      return sanitizeIntegrationListPayload(payload) as IntegrationApi[];
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (isSessionExpired(connectionsQuery.error)) {
      onSessionExpired();
    }
  }, [connectionsQuery.error, onSessionExpired]);

  const connectedConnections = useMemo(
    () => (connectionsQuery.data ?? []).filter((connection) => connection.connected && !connection.disabled),
    [connectionsQuery.data],
  );

  const importableFieldsQueries = useQueries({
    queries: connectedConnections.map((connection) => ({
      queryKey: ['importable-fields', connection.id, bootstrap.restUrls.accountingHub],
      queryFn: () =>
        fetchImportableFields({ baseUrl: bootstrap.restUrls.accountingHub, nonce: bootstrap.nonce }, connection.id),
      staleTime: 10 * 60 * 1000,
      retry: 1,
    })),
  });

  const supportedFieldKeysByConnectionId = useMemo((): Record<string, string[]> => {
    const result: Record<string, string[]> = {};
    connectedConnections.forEach((connection, index) => {
      const query = importableFieldsQueries[index];
      if (query?.data?.supported_field_keys) {
        result[connection.id] = query.data.supported_field_keys;
      }
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedConnections, importableFieldsQueries]);

  const resolvedPickerConnectionId = useMemo(() => {
    if (connectedConnections.length === 0) {
      return null;
    }
    if (pickerConnectionId && connectedConnections.some((c) => c.id === pickerConnectionId)) {
      return pickerConnectionId;
    }
    return connectedConnections[0].id;
  }, [connectedConnections, pickerConnectionId]);

  const selectedConnectionIds = useMemo(
    () =>
      Object.entries(state.selectionsByConnectionId)
        .filter(([, selection]) => selection.selectedRemoteCompanyIds.length > 0)
        .map(([connectionId]) => connectionId),
    [state.selectionsByConnectionId],
  );

  const selectedConnections = useMemo(
    () => connectedConnections.filter((connection) => selectedConnectionIds.includes(connection.id)),
    [connectedConnections, selectedConnectionIds],
  );

  const selectedConnectionsById = useMemo(
    () => Object.fromEntries(selectedConnections.map((connection) => [connection.id, connection])),
    [selectedConnections],
  );

  const queryClient = useQueryClient();

  const getEntityKeysFor = useCallback(
    (connectionId: string, remoteCompanyId: string) => {
      const m = getEntityKeysMapFromCache(queryClient, connectionId);
      return m.get(remoteCompanyId) ?? [];
    },
    [queryClient],
  );

  const setAttachTarget = useCallback((connectionId: string, remoteCompanyId: string, targetId: number | null) => {
    setState((prev) => {
      const prevConn = prev.attachTargetByRemoteId[connectionId] ?? {};
      const nextConn = { ...prevConn };
      if (targetId === null || targetId <= 0) {
        delete nextConn[remoteCompanyId];
      } else {
        nextConn[remoteCompanyId] = targetId;
      }
      return {
        ...prev,
        attachTargetByRemoteId: { ...prev.attachTargetByRemoteId, [connectionId]: nextConn },
      };
    });
  }, []);

  const setAllowCreate = useCallback((connectionId: string, remoteCompanyId: string, allow: boolean) => {
    setState((prev) => {
      const prevConn = prev.allowCreateByRemoteId[connectionId] ?? {};
      const nextConn = { ...prevConn };
      if (!allow) {
        delete nextConn[remoteCompanyId];
      } else {
        nextConn[remoteCompanyId] = true;
      }
      return {
        ...prev,
        allowCreateByRemoteId: { ...prev.allowCreateByRemoteId, [connectionId]: nextConn },
      };
    });
  }, []);

  // Build a remote_company_id → label lookup from whatever pages are cached in React Query.
  // This avoids extra API calls: the step 1 list already fetched these.
  const remoteCompanyLabelsByConnectionId = useMemo((): Record<string, Record<string, string>> => {
    const result: Record<string, Record<string, string>> = {};
    for (const connectionId of selectedConnectionIds) {
      const labels: Record<string, string> = {};
      const allCached = queryClient.getQueriesData<IntegrationCompaniesResponse>({
        queryKey: ['integration-companies', connectionId],
      });
      for (const [, data] of allCached) {
        if (!data) continue;
        for (const item of data.items) {
          if (item.remote_company_id && item.label) {
            labels[item.remote_company_id] = item.label;
          }
        }
      }
      result[connectionId] = labels;
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, selectedConnectionIds, importModalOpen]);

  // When supported keys arrive, clamp any persisted selections to the supported set.
  useEffect(() => {
    const entries = Object.entries(supportedFieldKeysByConnectionId);
    if (entries.length === 0) return;
    setState((prev) => {
      let changed = false;
      const next = { ...prev.selectionsByConnectionId };
      for (const [connectionId, supported] of entries) {
        const current = next[connectionId];
        if (!current) continue;
        const clamped = current.selectedFieldIds.filter((id) => supported.includes(id));
        const effective = clamped.length > 0 ? clamped : supported.slice();
        if (effective.join(',') !== current.selectedFieldIds.join(',')) {
          next[connectionId] = { ...current, selectedFieldIds: effective };
          changed = true;
        }
      }
      return changed ? { ...prev, selectionsByConnectionId: next } : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedFieldKeysByConnectionId]);

  const totalSelectedRemoteCompanies = useMemo(
    () =>
      Object.values(state.selectionsByConnectionId).reduce(
        (acc, selection) => acc + selection.selectedRemoteCompanyIds.length,
        0,
      ),
    [state.selectionsByConnectionId],
  );

  const { jobState, startJob, stopJob } = useImportJob({
    api: { baseUrl: bootstrap.restUrls.accountingHub, nonce: bootstrap.nonce },
    mode,
    companyId,
    selectedConnectionIds,
    selectedConnectionsById,
    onSessionExpired,
    getEntityKeysFor,
    remoteCompanyLabelsByConnectionId,
    getState,
    setState,
  });

  const { conflictsError, loadConflictsSummary } = useConflictsSummary({
    api: { baseUrl: bootstrap.restUrls.accountingHub, nonce: bootstrap.nonce },
    onSessionExpired,
    getState,
    setState,
  });

  const runError = jobState.startError;

  const updateSelection = useCallback(
    (connectionId: string, updater: (prev: IntegrationSelectionState) => IntegrationSelectionState) => {
      setState((prev) => {
        const supported = supportedFieldKeysByConnectionId[connectionId] ?? getProfileImportFieldIdsForConnection(connectionId).slice();
        const current = prev.selectionsByConnectionId[connectionId] ?? {
          selectedRemoteCompanyIds: [],
          selectedFieldIds: supported.slice(),
        };
        return {
          ...prev,
          selectionsByConnectionId: {
            ...prev.selectionsByConnectionId,
            [connectionId]: updater(current),
          },
          attachTargetByRemoteId: {},
          allowCreateByRemoteId: {},
          mapping: { ...prev.mapping, confirmedAtIso: null },
          run: { ...prev.run, status: 'idle', stopRequested: false, items: [], startedAtIso: null },
          conflicts: { status: 'idle', items: [] },
        };
      });
    },
    [supportedFieldKeysByConnectionId],
  );

  const toggleRemoteCompany = useCallback(
    (connectionId: string, remoteCompanyId: string) => {
      updateSelection(connectionId, (selection) => {
        const alreadySelected = selection.selectedRemoteCompanyIds.includes(remoteCompanyId);
        return {
          ...selection,
          selectedRemoteCompanyIds: alreadySelected
            ? selection.selectedRemoteCompanyIds.filter((id) => id !== remoteCompanyId)
            : [...selection.selectedRemoteCompanyIds, remoteCompanyId],
        };
      });
    },
    [updateSelection],
  );

  const setIntegrationFieldSelection = useCallback(
    (connectionId: string, selectedFieldIds: string[]) => {
      updateSelection(connectionId, (selection) => ({ ...selection, selectedFieldIds }));
    },
    [updateSelection],
  );

  const importModalWasOpenRef = useRef(false);

  const fetchMappingPreview = useCallback(
    async (connectionId: string) => {
      const selection = getState().selectionsByConnectionId[connectionId];
      const remoteCompanyIds = selection?.selectedRemoteCompanyIds ?? [];
      if (remoteCompanyIds.length === 0) {
        return;
      }
      const supported = supportedFieldKeysByConnectionId[connectionId] ?? getProfileImportFieldIdsForConnection(connectionId).slice();
      const selectedFieldIds = selection?.selectedFieldIds ?? supported;

      setMappingLoadingByConnectionId((prev) => ({ ...prev, [connectionId]: true }));
      setMappingErrorByConnectionId((prev) => ({ ...prev, [connectionId]: '' }));
      try {
        const entityKeysByRemoteId = getEntityKeysMapFromCache(queryClient, connectionId);
        const companies = buildPreviewCompanies(remoteCompanyIds, entityKeysByRemoteId);
        const response = await previewIntegrationMapping(
          { baseUrl: bootstrap.restUrls.accountingHub, nonce: bootstrap.nonce },
          connectionId,
          companies,
          selectedFieldIds,
        );
        setState((prev) => ({
          ...prev,
          mapping: {
            ...prev.mapping,
            itemsByConnectionId: {
              ...prev.mapping.itemsByConnectionId,
              [connectionId]: response.items,
            },
          },
        }));
      } catch (error) {
        if (isSessionExpired(error)) {
          onSessionExpired();
          return;
        }
        const message = extractErrorMessage(error, __('Prévisualisation impossible.'));
        setMappingErrorByConnectionId((prev) => ({ ...prev, [connectionId]: message }));
      } finally {
        setMappingLoadingByConnectionId((prev) => ({ ...prev, [connectionId]: false }));
      }
    },
    [
      bootstrap.nonce,
      bootstrap.restUrls.accountingHub,
      getState,
      onSessionExpired,
      queryClient,
      supportedFieldKeysByConnectionId,
    ],
  );

  useEffect(() => {
    const justOpened = importModalOpen && !importModalWasOpenRef.current;
    importModalWasOpenRef.current = importModalOpen;

    if (!justOpened || mode !== 'bulk' || stepIndex !== 1) {
      return;
    }
    if (selectedConnectionIds.length === 0) {
      return;
    }

    flushSync(() => {
      setState((prev) => {
        const nextItems = { ...prev.mapping.itemsByConnectionId };
        for (const connectionId of selectedConnectionIds) {
          delete nextItems[connectionId];
        }
        return {
          ...prev,
          mapping: {
            ...prev.mapping,
            itemsByConnectionId: nextItems,
          },
        };
      });
    });

    selectedConnectionIds.forEach((connectionId) => {
      void fetchMappingPreview(connectionId);
    });
  }, [importModalOpen, mode, stepIndex, selectedConnectionIds, fetchMappingPreview, setState]);

  const mappingPreviewByConnectionId = useMemo((): Record<string, MappingPreviewItem[]> => {
    if (mode === 'single') {
      return Object.fromEntries(
        selectedConnectionIds.map((connectionId) => {
          const remoteIds = state.selectionsByConnectionId[connectionId]?.selectedRemoteCompanyIds ?? [];
          return [
            connectionId,
            remoteIds.map((remoteCompanyId) => ({
              remote_company_id: remoteCompanyId,
              target_company_id: companyId,
              reason: 'linked' as const,
            })),
          ];
        }),
      );
    }
    return state.mapping.itemsByConnectionId;
  }, [companyId, mode, selectedConnectionIds, state.mapping.itemsByConnectionId, state.selectionsByConnectionId]);

  useEffect(() => {
    if (stepIndex !== 2 || state.run.status === 'running' || state.conflicts.status !== 'idle') {
      return;
    }
    void loadConflictsSummary();
  }, [loadConflictsSummary, state.conflicts.status, state.run.status, stepIndex]);

  useEffect(() => {
    const prev = prevRunStatusRef.current;
    prevRunStatusRef.current = state.run.status;
    if (stepIndex !== 1) {
      return;
    }
    if (prev !== 'running') {
      return;
    }
    if (state.run.status === 'running') {
      return;
    }
    if (state.run.status !== 'completed') {
      return;
    }
    setImportModalOpen(false);
    setStepIndex(2);
    if (portalMode && patchPortalSearch) {
      patchPortalSearch(
        {
          importModal: null,
          step: IMPORT_STEP_SLUGS[2],
        },
        'replace',
      );
    }
  }, [portalMode, patchPortalSearch, state.run, stepIndex]);

  const canGoNext = useMemo(() => {
    if (stepIndex === 0) {
      return mode === 'single' ? totalSelectedRemoteCompanies === 1 : totalSelectedRemoteCompanies > 0;
    }
    if (stepIndex === 1) {
      return selectedConnections.every(
        (connection) => (state.selectionsByConnectionId[connection.id]?.selectedFieldIds ?? []).length > 0,
      );
    }
    if (stepIndex === 2) {
      return true;
    }
    return true;
  }, [mode, selectedConnections, state.selectionsByConnectionId, stepIndex, totalSelectedRemoteCompanies]);

  const isLastStep = stepIndex === IMPORT_WIZARD_LAST_STEP_INDEX;

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: __('Mes dossiers'), onClick: () => navigateTo('/companies') },
    { label: __('Importer depuis un connecteur') },
  ];

  const handleWizardNext = () => {
    if (stepIndex === 1) {
      setState((prev) => ({
        ...prev,
        attachTargetByRemoteId: {},
        allowCreateByRemoteId: {},
      }));
      if (portalMode && patchPortalSearch) {
        patchPortalSearch({ importModal: 'confirm' }, 'push');
      } else {
        setImportModalOpen(true);
      }
      return;
    }
    if (!isLastStep) {
      navigateStepDelta(Math.min(IMPORT_WIZARD_LAST_STEP_INDEX, stepIndex + 1));
      return;
    }
    clearState();
    setState(createDefaultState(mode, companyId));
    setStepIndex(0);
    setPickerConnectionId(null);
    setImportModalOpen(false);
    if (portalMode && patchPortalSearch) {
      patchPortalSearch({ step: null, importModal: null }, 'replace');
    }
    if (mode === 'single' && companyId !== null) {
      navigateTo(`/companies/company/${companyId}`);
      return;
    }
    navigateTo('/companies');
  };

  const handleConfirmImport = async () => {
    const confirmedAtIso = new Date().toISOString();
    flushSync(() => {
      setState((prev) => ({
        ...prev,
        mapping: { ...prev.mapping, confirmedAtIso },
      }));
    });
    await startJob();
  };

  const nextLabel = stepIndex === 1 ? __('Importer') : isLastStep ? __('Terminer') : __('Suivant');
  const primaryAriaLabel =
    stepIndex === 1 ? __('Importer (wizard)') : isLastStep ? __('Terminer (wizard)') : undefined;

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />
      {connectionsQuery.isLoading ? <p>{__('Chargement des connexions...')}</p> : null}
      {connectionsQuery.error ? (
        <div role="alert">
          <p>{extractErrorMessage(connectionsQuery.error, __('Impossible de charger les connexions.'))}</p>
          <button type="button" className="btn-secondary" onClick={() => connectionsQuery.refetch()}>
            {__('Réessayer')}
          </button>
        </div>
      ) : null}
      {!connectionsQuery.isLoading && !connectionsQuery.error ? (
        <WizardShell
          sidebar={
            connectedConnections.length > 0 && resolvedPickerConnectionId !== null ? (
              <ImportWizardIntegrationSidebar
                connections={connectedConnections}
                selectedConnectionId={resolvedPickerConnectionId}
                onSelectConnection={setPickerConnectionId}
              />
            ) : null
          }
          canGoBack={stepIndex > 0 || importModalOpen}
          canGoNext={canGoNext}
          nextLabel={nextLabel}
          primaryAriaLabel={primaryAriaLabel}
          onBack={() => {
            if (importModalOpen) {
              closeConfirmModalOnly();
              return;
            }
            navigateStepDelta(Math.max(0, stepIndex - 1));
          }}
          onNext={handleWizardNext}
          onCancel={handleCancelWizard}
          onReset={() => {
            clearState();
            setState(createDefaultState(mode, companyId));
            setStepIndex(0);
            setPickerConnectionId(null);
            setImportModalOpen(false);
            if (portalMode && patchPortalSearch) {
              patchPortalSearch({ step: null, importModal: null }, 'replace');
            }
          }}
        >
          {stepIndex === 0 ? (
            <SelectIntegrationCompaniesStep
              baseUrl={bootstrap.restUrls.accountingHub}
              nonce={bootstrap.nonce}
              connections={connectedConnections}
              activeConnectionId={resolvedPickerConnectionId ?? ''}
              selectionsByConnectionId={state.selectionsByConnectionId}
              onToggleRemoteCompany={toggleRemoteCompany}
            />
          ) : null}

          {stepIndex === 1 ? (
            <SelectDataStep
              connections={connectedConnections}
              activeConnectionId={resolvedPickerConnectionId ?? ''}
              selectedConnections={selectedConnections}
              selectionsByConnectionId={state.selectionsByConnectionId}
              supportedFieldKeysByConnectionId={supportedFieldKeysByConnectionId}
              onSelectionChange={setIntegrationFieldSelection}
            />
          ) : null}

          {stepIndex === 2 ? (
            <>
              {conflictsError !== '' ? <p role="alert">{conflictsError}</p> : null}
              <ConflictsPlaceholderStep
                portalPath={normalizePath(bootstrap.portalPath)}
                mode={mode}
                targetCompanyId={companyId}
                run={state.run}
                conflicts={state.conflicts}
                onRetryFetch={loadConflictsSummary}
              />
            </>
          ) : null}

        </WizardShell>
      ) : null}

      <ImportConfirmModal
        open={importModalOpen}
        onClose={() => closeConfirmModalOnly()}
        mode={mode}
        targetCompanyId={companyId}
        selectedConnections={selectedConnections}
        selectionsByConnectionId={state.selectionsByConnectionId}
        previewByConnectionId={mappingPreviewByConnectionId}
        loadingByConnectionId={mappingLoadingByConnectionId}
        errorByConnectionId={mappingErrorByConnectionId}
        onRetryConnection={fetchMappingPreview}
        totalRemoteCompanies={totalSelectedRemoteCompanies}
        remoteCompanyLabelsByConnectionId={remoteCompanyLabelsByConnectionId}
        attachTargetByRemoteId={state.attachTargetByRemoteId}
        allowCreateByRemoteId={state.allowCreateByRemoteId}
        onAttachChange={setAttachTarget}
        onAllowCreateChange={setAllowCreate}
        api={{ baseUrl: bootstrap.restUrls.accountingHub, nonce: bootstrap.nonce }}
        run={state.run}
        runError={runError}
        jobState={jobState}
        onConfirmImport={handleConfirmImport}
        onStopRun={stopJob}
      />
    </>
  );
};
